import { Input, Link } from "react-ui";
import * as React from "react";
import { useState } from "react";

import { FiasAddress, getGuidAddressValidationResult, getModelAddressByGuid } from "../../../Utils/FiasAddressUtils";
import { Caption, Row } from "../FiasFnsManualEditor.styles";
import { Tooltip } from "../../../../../Internals/Tooltip/Tooltip";
import { FiasApi } from "../../../FiasInput/FiasApi";
import { useAsyncEffect } from "../../../../../../../../Common/UseAsyncEffect";
import { ParsedExtendedFiasField } from "../../../FiasInput/Types/FiasTypes";
import { useMetrics } from "../../../../../Internals/Metrics/MetricsProviderContext";
import { FIAS_FILL_BY_ID } from "../../../../../metricsAction";
import { useTrackWrongId } from "../../../FiasMetrics";

interface GuidProps {
    value: FiasAddress;
    onValueChange: (nextValue: FiasAddress) => void;
    extendedFields: ParsedExtendedFiasField[];
    apiClient: FiasApi;
    path: string;
}

export function Guid({ value, onValueChange, extendedFields, apiClient, path }: GuidProps): JSX.Element | null {
    const [internalWarning, setInternalWarning] = useState<string>("");
    const [internalError, setInternalError] = useState<string>("");

    const hasInternalError = React.useRef(Boolean(internalError));
    const hasInternalWarning = React.useRef(Boolean(internalWarning));
    useTrackWrongId(hasInternalError, hasInternalWarning);

    const currentValue = value[path] ?? "";

    useAsyncEffect(async () => {
        const { error, warning } = await getGuidAddressValidationResult(
            currentValue,
            value,
            extendedFields,
            path,
            apiClient
        );

        setInternalError(error);
        setInternalWarning(warning);
        hasInternalError.current = Boolean(error);
        hasInternalWarning.current = Boolean(warning);
    }, [value]);

    const track = useMetrics();

    const fillByGuid = React.useCallback(async () => {
        const nextAddress = await getModelAddressByGuid(currentValue, extendedFields, apiClient);
        onValueChange({ ...nextAddress, [path]: currentValue });
        track(FIAS_FILL_BY_ID);
    }, [value, extendedFields]);

    const tooltipTrigger = internalWarning ? "hover&focus" : "closed";

    return (
        <Row>
            <Caption>Уникальный идентификатор адресного объекта в ГАР</Caption>
            <Tooltip pos={"top center"} trigger={tooltipTrigger} value={internalWarning}>
                <Input
                    warning={Boolean(internalWarning)}
                    width={350}
                    maxLength={36}
                    value={currentValue}
                    placeholder={"Код ФИАС"}
                    data-tid={"IdNum"}
                    mask="********-****-****-****-************"
                    onValueChange={nextValue => onValueChange({ ...value, [path]: nextValue ?? "" })}
                />
                <Link
                    style={{ display: "block" }}
                    data-tid="FillByIdNumLink"
                    onClick={fillByGuid}
                    disabled={Boolean(internalError)}>
                    {"Заполнить адрес по идентификатору"}
                </Link>
            </Tooltip>
        </Row>
    );
}
